import {
    AfterViewInit,
    Directive,
    ElementRef,
    EventEmitter,
    Input,
    NgZone,
    OnChanges,
    OnDestroy,
    Output,
} from '@angular/core';

/** Emitted whenever the marks are re-applied or the active match moves. */
export interface GableMatchState {
    count: number;
    /** 0-based index of the active mark; -1 when there are no matches. */
    activeIndex: number;
}

/**
 * Highlights occurrences of a search term inside already-rendered message
 * markup (SD-6742). Applied to the messages container; when a conversation is
 * opened from a search, this marks the matched words AND scrolls the first
 * match into view with a brief flash on its bubble.
 *
 * Why a TreeWalker instead of the `highlight` pipe: message bubbles render
 * markdown to real HTML (links, code, lists). Running a regex over that HTML
 * string would corrupt tags/attributes. Walking TEXT NODES only — and skipping
 * code/pre — wraps matches safely without ever touching markup.
 */
@Directive({
    selector: '[appGableMessageHighlight]',
    standalone: true,
})
export class GableMessageHighlightDirective implements AfterViewInit, OnChanges, OnDestroy {
    /** The active search term. Empty/whitespace clears any existing marks.
     *  Named to match the selector so no @Input alias is needed (no-input-rename). */
    @Input() appGableMessageHighlight: string | null = null;

    /**
     * Match count + active index for the search field's "3 / 11" counter.
     * Emitted inside the Angular zone — `_apply()` deliberately runs outside
     * it, and an emit from there would never tick change detection.
     */
    @Output() matchesChanged = new EventEmitter<GableMatchState>();

    private _raf: number | null = null;
    private _marks: HTMLElement[] = [];
    private _activeIndex = -1;

    constructor(
        private readonly _el: ElementRef<HTMLElement>,
        private readonly _zone: NgZone,
    ) {}

    ngAfterViewInit(): void {
        this._schedule();
    }

    ngOnChanges(): void {
        this._schedule();
    }

    ngOnDestroy(): void {
        this._cancel();
        // The host outlives this directive (the thread unmounts while the search
        // box stays), so leave it with a zeroed counter rather than a stale one.
        this._marks = [];
        this._activeIndex = -1;
        this._emit();
    }

    /**
     * Re-apply the marks because the markup under them changed — a streamed
     * delta re-sets a bubble's `[innerHTML]`, which detaches every mark inside
     * it. Preserves the active position and does NOT scroll or flash: a refresh
     * is maintenance, not a new search, and yanking the thread back to match 1
     * on every streamed frame would be worse than the stale counter it fixes.
     *
     * Free unless there is something to maintain — the TreeWalker below only
     * runs when a term is active or marks are still on the page — so the host
     * can call this on every message emission.
     */
    refresh(): void {
        if ((this.appGableMessageHighlight ?? '').trim() === '' && this._marks.length === 0) {
            return;
        }
        this._schedule(true);
    }

    /** Chrome-style next/previous, wrapping at both ends. */
    next(): void {
        this._move(1);
    }

    prev(): void {
        this._move(-1);
    }

    private _move(step: number): void {
        // A streamed delta re-sets the bubble's `[innerHTML]`, which silently
        // detaches the marks inside it — `_marks` is only rebuilt when the TERM
        // changes, so without this the counter advances over nodes that are no
        // longer in the document and `scrollIntoView` is a no-op.
        this._prune();
        if (this._marks.length === 0) {
            this._activeIndex = -1;
            this._emit();
            return;
        }
        const total = this._marks.length;
        this._activeIndex = (this._activeIndex + step + total) % total;
        this._paintActive();
        this._emit();
    }

    /** Drop marks the document no longer owns, keeping `_activeIndex` in range. */
    private _prune(): void {
        const live = this._marks.filter((m) => m.isConnected);
        if (live.length === this._marks.length) {
            return;
        }
        this._marks = live;
        if (this._activeIndex >= live.length) {
            this._activeIndex = live.length > 0 ? 0 : -1;
        }
    }

    /**
     * Defer to the next frame so the markdown `innerHTML` bindings are in the
     * DOM before we walk them. Runs outside Angular — pure DOM work, no state.
     */
    private _schedule(preserveActive = false): void {
        this._cancel();
        this._zone.runOutsideAngular(() => {
            this._raf = requestAnimationFrame(() => {
                this._raf = null;
                this._apply(preserveActive);
            });
        });
    }

    private _cancel(): void {
        if (this._raf !== null) {
            cancelAnimationFrame(this._raf);
            this._raf = null;
        }
    }

    private _apply(preserveActive = false): void {
        const root = this._el.nativeElement;
        // `_clear()` zeroes it, so read it first.
        const previousActive = preserveActive ? this._activeIndex : -1;
        this._clear(root);

        const term = (this.appGableMessageHighlight ?? '').trim();
        if (term === '') {
            // `_clear()` above already zeroed the state; publish it. Returning
            // silently leaves the host showing the PREVIOUS search's "3 / 11"
            // on the first keystroke of the next one, which contradicts the
            // template's documented "0 / 0 is the resting state".
            this._emit();
            return;
        }

        // Highlight the full literal query string ("unit 113"), not each word —
        // the server matches the exact phrase for multi-word terms, so the
        // highlight mirrors exactly what was searched (no per-word noise).
        const wrapped = this._highlight(root, term.toLowerCase());
        // A new TERM restarts the walk, so the previous active index is
        // meaningless — a keystroke lands the user back on match 1. A REFRESH
        // (same term, changed markup) keeps the user where they were, clamped in
        // case the change removed matches below them.
        this._marks = Array.from(root.querySelectorAll<HTMLElement>('mark.search-highlight'));
        if (wrapped === 0) {
            this._activeIndex = -1;
        } else if (preserveActive && previousActive >= 0) {
            this._activeIndex = Math.min(previousActive, wrapped - 1);
        } else {
            this._activeIndex = 0;
        }
        this._paintActive(!preserveActive);
        if (wrapped > 0 && !preserveActive) {
            // `_paintActive()` has already scrolled the first mark into view, so
            // this only adds the bubble flash.
            this._flashFirstBubble(root);
        }
        this._emit();
    }

    /** Chrome's orange-vs-yellow: only one mark reads as the current hit. */
    private _paintActive(scroll = true): void {
        this._marks.forEach((m, i) => m.classList.toggle('is-active', i === this._activeIndex));
        const active = this._marks[this._activeIndex];
        if (active !== undefined && scroll) {
            active.scrollIntoView({ block: 'center', behavior: 'smooth' });
        }
    }

    /** Re-enter the zone: `_apply()` runs outside it, so a bare emit is inert. */
    private _emit(): void {
        const state: GableMatchState = { count: this._marks.length, activeIndex: this._activeIndex };
        this._zone.run(() => this.matchesChanged.emit(state));
    }

    /** Unwrap any marks WE added and re-join the split text. */
    private _clear(root: HTMLElement): void {
        this._marks = [];
        this._activeIndex = -1;
        const marks = root.querySelectorAll('mark.search-highlight');
        marks.forEach((m) => m.replaceWith(document.createTextNode(m.textContent ?? '')));
        if (marks.length > 0) {
            root.normalize();
        }
    }

    /** Wrap every case-insensitive occurrence of `lc` (the full query) in text nodes. */
    private _highlight(root: HTMLElement, lc: string): number {
        const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
            acceptNode: (node) => {
                const parent = node.parentElement;
                if (parent === null) {
                    return NodeFilter.FILTER_REJECT;
                }
                // Never highlight inside code/pre (would mangle snippets) or
                // inside a mark we already placed.
                if (parent.closest('code, pre, mark, script, style') !== null) {
                    return NodeFilter.FILTER_REJECT;
                }
                return (node.nodeValue ?? '').toLowerCase().includes(lc)
                    ? NodeFilter.FILTER_ACCEPT
                    : NodeFilter.FILTER_REJECT;
            },
        });

        const targets: Text[] = [];
        let current: Node | null;
        while ((current = walker.nextNode()) !== null) {
            targets.push(current as Text);
        }

        let count = 0;
        for (const textNode of targets) {
            count += this._wrap(textNode, lc);
        }
        return count;
    }

    private _wrap(textNode: Text, lc: string): number {
        const text = textNode.nodeValue ?? '';
        const lower = text.toLowerCase();
        const frag = document.createDocumentFragment();
        let cursor = 0;
        let idx = lower.indexOf(lc, cursor);
        let count = 0;

        while (idx !== -1) {
            if (idx > cursor) {
                frag.appendChild(document.createTextNode(text.slice(cursor, idx)));
            }
            const mark = document.createElement('mark');
            mark.className = 'search-highlight';
            mark.textContent = text.slice(idx, idx + lc.length);
            frag.appendChild(mark);
            count++;
            cursor = idx + lc.length;
            idx = lower.indexOf(lc, cursor);
        }

        if (cursor < text.length) {
            frag.appendChild(document.createTextNode(text.slice(cursor)));
        }
        textNode.replaceWith(frag);
        return count;
    }

    /** Center the first match and flash its bubble so the eye finds it. */
    private _flashFirstBubble(root: HTMLElement): void {
        const first = root.querySelector<HTMLElement>('mark.search-highlight');
        if (first === null) {
            return;
        }
        const bubble = first.closest<HTMLElement>('.message');
        if (bubble !== null) {
            bubble.classList.remove('gable-match-flash');
            // Force reflow so re-adding the class restarts the animation.
            void bubble.offsetWidth;
            bubble.classList.add('gable-match-flash');
        }
    }
}
